/* eslint-disable @typescript-eslint/no-explicit-any */
import { axiosInstances } from '@api/config/axios';
import { routes as API } from '@api/config/routes';
import { getDigilockerVerificationDetails } from '@api/form-verification';
import aadhaarArt from '@assets/verification/aadhaar-art.svg';
import dlArt from '@assets/verification/dl-art.svg';
import passportArt from '@assets/verification/passport-art.svg';
import StandardButton from '@components/ui/buttons/standard-button';
import TopBackBtnWrapper from '@components/ui/top-back-btn-wrapper';
import { ProfileVerifications } from '@global/constants/verification';
import { getAccessToken } from '@global/helpers/general';
import { mixpanel, trackingEvents } from '@global/monitoring/mixpanel';
import { Box, Checkbox, Typography } from '@mui/material';
import { RootState } from '@redux/store';
import { navigateTo } from '@services/navigation';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

export default function Digilocker() {
  const location = useLocation();
  const { selectedAuthOption: config, disableVerification } = location.state ?? {};
  const [consentProvided, setConsentProvided] = useState(false);
  const [selectedDigilockerDocType, setSelectedDigilockerDocType] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { currentVerification } = useSelector((s: RootState) => s.misc);
  const { configs } = useSelector((s: RootState) => s.verification) ?? {};
  const { formId, txnId: formTxnId, orgInfo } = useSelector((s: RootState) => s.formData);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const txnId = searchParams.get('transaction_id');
    setConsentProvided(!!txnId);
    const docType = configs[currentVerification]?.additionalInfo.authOptions.find(
      (x: { type: string }) => x.type === 'digilocker'
    )?.docType;

    setSelectedDigilockerDocType(docType);

    if (txnId) {
      const fetchDetails = async () => {
        setLoading(true);
        const res = await getDigilockerVerificationDetails({
          data: {
            transactionId: txnId,
            digiLocker: true
          },
          verificationName: currentVerification
        });
        setLoading(false);
        if (res?.code === 0) {
          mixpanel.track(trackingEvents.profileVerification.events.digilockerAuthCompleted, {
            verification: currentVerification,
            auth_success: true,
            aadhaar_fetched: true
          });
          mixpanel.track(trackingEvents.profileVerification.events.digilockerDataStored, {
            verification: currentVerification,
            fields_stored: Object.keys(res?.data ?? {})
          });
          mixpanel.track(trackingEvents.profileVerification.events.completed, {
            identity_method: 'digilocker',
            verification: currentVerification
          });
          navigateTo(`/ekyc/${formId}/${formTxnId}?orgId=${orgInfo?.orgId}`);
        } else {
          mixpanel.track(trackingEvents.profileVerification.events.digilockerAuthFailed, {
            verification: currentVerification,
            auth_success: false,
            error_code: res?.code,
            error_message: res?.message,
            user_cancelled: false
          });
        }
      };
      fetchDetails();
    }
  }, [currentVerification]);

  const handleOpenDigilocker = async () => {
    try {
      const response = await axiosInstances.verificationV1?.post(
        API.digilocker.preVerify,
        {
          redirectUrl: window.location.origin + '/digilocker',
          docTypes: ['ADHAR']
        },
        {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`
          }
        }
      );

      mixpanel.track(trackingEvents.selfVerification.events.digilocker.started, {
        data: selectedDigilockerDocType,
        type: 'Single',
        verification: config?.key ?? 'digilocker',
        status: 'Started'
      });
      mixpanel.track(trackingEvents.profileVerification.events.digilockerRedirectInitiated, {
        docType: selectedDigilockerDocType,
        verification: config?.key ?? 'digilocker'
      });

      const redirectURL = response?.data?.data?.authUrl;
      if (redirectURL) {
        window.location.replace(redirectURL);
      }
    } catch (error) {
      console.error('Error opening Digilocker:', error);
    }
  };

  const getImgSrc = () => {
    if (currentVerification === ProfileVerifications?.AADHAAR) {
      return aadhaarArt;
    }
    return currentVerification === 'dl' ? dlArt : passportArt;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexFlow: 'column', backgroundColor: 'bg.light' }}>
      <TopBackBtnWrapper
        title={`${configs[currentVerification]?.metadata.label} Verification`}
        onBackClick={() => navigateTo(-2)}
      />
      <Box
        sx={{
          padding: { xs: '1rem 1.75rem', md: '1.5rem 2rem' },
          pb: { xs: '.4rem', md: '1.5rem' },
          borderRadius: '.8rem',
          flex: 1,
          overflowY: 'auto',
          justifyContent: 'space-between',
          display: 'flex',
          flexFlow: 'column'
        }}>
        <Box sx={{ m: 2 }}>
          <img src={getImgSrc()} style={{ width: '100%' }} alt={`digilocker-art`} />
          <Typography variant="h4" sx={{ fontWeight: '400', m: 1 }}>
            Please select Open Digilocker to verify details
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexFlow: { xs: 'column', md: 'row' },
            mb: { xs: 0, md: '.7rem' },
            rowGap: '1rem'
          }}>
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'column',
              justifyContent: 'space-between',
              rowGap: '1rem',
              flex: 1,
              padding: { xs: 0, md: '0 5.1%' }
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '.9rem',
                mb: { xs: '.7rem', md: '1rem' },
                height: '3.75rem',
                padding: '2rem 0',
                width: {
                  xs: '100%',
                  md: '100%'
                }
              }}>
              <Checkbox
                checked={consentProvided}
                onChange={(e) => setConsentProvided(e.target.checked)}
                sx={{
                  height: { xs: '3.15rem', md: '3.5rem' },
                  width: { xs: '3.15rem', md: '3.5rem' }
                }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: '400', fontSize: '1.2rem' }}>
                I agree that this data is shared with the informed consent of the owner/user for the
                purpose of verification and processing.
              </Typography>
            </Box>
            <StandardButton
              loading={loading}
              disabled={
                disableVerification ||
                !consentProvided ||
                ((config?.key === 'digilocker' || !config) &&
                  selectedDigilockerDocType?.length === 0)
              }
              onClick={() => handleOpenDigilocker()}
              padding={'.9rem 2.2rem'}>
              {'Open Digilocker'}
            </StandardButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
